import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { MessageSquare } from 'lucide-react'
import { Button } from '../components/ui/button'
import Modal from '../components/ui/modal'
import { useLanguage } from '../context/LanguageContext'
import { useTenant } from '../context/TenantContext'
import { useClient } from '../context/ClientContext'
import apiService from '../services/api.service'

const ContactUs = () => {
  const navigate = useNavigate()
  const { tenanturlname } = useParams()
  const { t } = useLanguage()
  const { tenant } = useTenant()
  const { client, updateClient, isAuthenticated } = useClient()
  const [loader, setLoader] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [contactDisabled, setContactDisabled] = useState(false)

  useEffect(() => {
    if (client && tenant) {
      setContactDisabled(
        Boolean(client?.iscontacted) &&
          tenant?.tenantmasterconfig?.contactus?.allow_multiple === false
      )
    }
  }, [client, tenant])

  const sendContactUs = async () => {
    setLoader(true)
    try {
      await apiService.post('/api/clients/contactus', {
        clientid: client.clientId,
        tenantid: tenant.tenantid,
      })

      setTimeout(() => {
        setShowModal(true)
        setLoader(false)
        // Update client state to mark as contacted
        if (updateClient) {
          updateClient({ ...client, iscontacted: true })
        }
      }, 2000)
    } catch (error) {
      console.error('OW-ERR-CONTACTUS: ', error)
      setTimeout(() => {
        setLoader(false)
      }, 2000)
    }
  }

  const handleModalClose = () => {
    setShowModal(false)
    navigate(`/${tenanturlname}/account-settings`)
  }

  return (
    <div className="inquiry-form-page">
      <div className="flex flex-col items-center w-full mx-auto mt-8 px-4">
        {/* Header */}
        <div className="flex items-center gap-2 w-full mb-6">
          <MessageSquare className="w-4 h-4" />
          <h1 className="text-xl font-bold">
            {t('contactUs.title') || 'Contact Us'}
          </h1>
        </div>

        {/* Contact Us Card */}
        <div className="w-full bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden p-6">
          <p className="text-sm text-gray-600 mb-6">
            {t('contactUs.description') ||
              "If you're interested in becoming part of the program, simply let us know! Just click 'Send' below and we'll notify the Visa team of your interest."}
          </p>

          {isAuthenticated && (
            <Button
              onClick={sendContactUs}
              disabled={contactDisabled || loader}
              className="w-full sm:w-auto"
            >
              {loader
                ? (t('contactUs.sending') || 'Sending...')
                : contactDisabled
                ? (t('contactUs.alreadySent') || 'You have already sent the request.')
                : (t('contactUs.sendButton') || 'Send')}
            </Button>
          )}
        </div>
      </div>

      {/* Success Modal */}
      <Modal
        open={showModal}
        onClose={handleModalClose}
        type="info"
        title={t('contactUs.title') || 'Contact Us'}
        message={t('contactUs.successMessage') || 'Thank you.'}
        onConfirm={handleModalClose}
        confirmText={t('common.ok') || 'OK'}
      />

      {/* Loading Overlay */}
      {loader && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-white"></div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ContactUs
